"use client";
import { useMediaSize } from "@/hooks/useMediaSize";
import { useCaseList, type CaseItem, type Deliverable } from "@/hooks/useCaseList";
import UseCaseBackground from "@/public/imaegs/user-case-bg.svg";
import ReportIcon from "@/public/imaegs/report-icon.svg";
import QuoteIcon from "@/public/imaegs/quote-icon.svg";
import { gsap } from "gsap";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Image from "next/image";
import VideoPlayer from "@/components/VideoPlayerBox";
import VideoModal from "@/components/VideoModal";
import { useState, useEffect } from "react";
import CustomSegmented from "../CustomSegmented";
import { FellouButton } from "@/ui-components-next/FellouButton";
import "./styles.scss";
import "./styles.mobile.scss";

// Define proper interfaces for the case items
// 使用 useCaseList hook 中已定义的类型
// CaseItem 和 Deliverable 类型已在 useCaseList.ts 中定义

const placeholderData = Array(12).fill({ isPlaceholder: true });

const SkeletonItem = () => {
  return (
    <div className="use-case-card skeleton">
      <div className="use-case-card-image skeleton-box">
        <div className="skeleton-image"></div>
      </div>
      <div className="use-case-card-conten skeleton-conten">
        <div className="skeleton-text"></div>
        <div className="skeleton-text"></div>
        <div className="skeleton-text"></div>
        <div className="skeleton-text"></div>
      </div>
    </div>
  );
};

gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);
export default function UseCase() {
  const { isPhoneMedia } = useMediaSize();
  const [displayedItems, setDisplayedItems] = useState<CaseItem[]>([]);
  const [showAll, setShowAll] = useState(false);
  const itemsPerPage = 6; // Number of items to show per page

  // Add states for user cases display
  const [displayedUserItems, setDisplayedUserItems] = useState<CaseItem[]>([]);
  const [showAllUserItems, setShowAllUserItems] = useState(false);
  const userItemsPerPage = 6; // Number of user items to show per page

  useEffect(() => {
    if (!isPhoneMedia) {
      ScrollTrigger.config({
        ignoreMobileResize: true,
        syncInterval: 60,
      });
    }
    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [isPhoneMedia]);

  const {
    activeCategory,
    activeTag,
    tagOptions,
    categoryOptions,
    filteredItems,
    userfilteredItems,
    setActiveTag,
    setActiveCategory,
  } = useCaseList();

  // Reset view when category or tag changes
  useEffect(() => {
    setShowAll(false);
    setShowAllUserItems(false);
  }, [activeCategory, activeTag]);

  // Update displayed items when filteredItems or showAll changes
  useEffect(() => {
    if (showAll) {
      setDisplayedItems(filteredItems);
    } else {
      // Always show only itemsPerPage items until View All is clicked
      setDisplayedItems(filteredItems.slice(0, itemsPerPage));
    }
  }, [filteredItems, showAll, itemsPerPage]);

  // Update displayed user items when userfilteredItems or showAllUserItems changes
  useEffect(() => {
    if (showAllUserItems) {
      setDisplayedUserItems(userfilteredItems);
    } else {
      // Always show only userItemsPerPage items until View All is clicked
      setDisplayedUserItems(userfilteredItems.slice(0, userItemsPerPage));
    }
  }, [userfilteredItems, showAllUserItems, userItemsPerPage]);

  // Remove the scroll event listener completely - items will only load when View All is clicked

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tag = urlParams.get("tag");
    if (tag) {
      setActiveTag(tag);
    }

    // 处理锚点定位 - 立即滚动，不等待数据加载
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash) as HTMLElement;
        if (element) {
          // 计算滚动位置，让标题显示在视窗较靠上的位置
          const elementTop = element.offsetTop;
          const offset = 50; // 减少偏移量，让标题更靠上
          window.scrollTo({
            top: elementTop - offset,
            behavior: "smooth",
          });
        }
      }, 50); // 减少延迟时间，让滚动更快
    }
  }, []);

  useEffect(() => {
    // Handle hash routing
    if (
      userfilteredItems.length > 0 &&
      typeof window !== "undefined" &&
      window.location.hash === "#user-cases-section"
    ) {
      setTimeout(() => {
        const element = document.getElementById("user-cases-section-anchor");
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 300); // Add a small delay to ensure the element is rendered
    }
  }, [userfilteredItems.length]);

  const renderContent = (type: string, src: string) => {
    switch (type) {
      case "video":
        return (
          <div className={"fellou-smart-card-item-video"}>
            <div className="fellou-smart-card-item-video-mask">
              <div className="fellou-smart-card-item-video-mask-play">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <path
                    d="M8 26.9276V5.07228C8 4.02504 9.15193 3.38658 10.04 3.94162L27.5243 14.8693C28.3599 15.3915 28.3599 16.6084 27.5243 17.1307L10.04 28.0583C9.15193 28.6133 8 27.9749 8 26.9276Z"
                    fill="white"
                  />
                </svg>
              </div>
            </div>
            <div className="fellou-smart-card-item-video-img-bg">
              <Image
                className="use-case-pin-round-bg-img"
                src={"https://cdn.fellou.ai/images/images/home/video-bg.png"}
                alt="fellou"
                fill
                priority
              />
              <VideoPlayer videoSrc={src} autoPlay={false} height={null} />
            </div>
          </div>
        );
      case "img":
        return (
          <div className="fellou-smart-card-item-image">
            <Image src={src} alt="fellou" fill priority unoptimized />
          </div>
        );
      default:
        return null;
    }
  };

  const [adopterModalStatus, setAdopterModalStatus] = useState(false);
  const [curentVideo, setCurentVideo] = useState({
    src: "",
    title: "",
    description: "",
    deliverables: [] as Deliverable[],
  });

  // console.log(filteredItems, 'filteredItemsfilteredItems dft')
  // console.log(displayedItems, 'displayedItems dft')

  const handleViewMore = (type: "official" | "user" = "official") => {
    if (type === "official") {
      setShowAll(true);
    } else if (type === "user") {
      setShowAllUserItems(true);
    }
  };

  return (
    <div className="use-case-page">
      <div className="use-case-pin-round-bg">
        <Image className="use-case-pin-round-bg-img" src={UseCaseBackground} alt="fellou" fill priority />
      </div>

      <div className="use-case-page-content">
        <div className="screen-1-box">
          <div className="screen-1-info-box">
            <div className="use-case-title">
              <div className="use-case-title-1">Fellou Use Cases</div>
              <div className="use-case-title-2">See real scenarios where Fellou handles research and actions.</div>
            </div>

            <div className="category-segmented">
              <CustomSegmented
                isFixedTags={true}
                options={tagOptions}
                value={activeTag}
                onChange={setActiveTag}
                block
              />
            </div>
            {activeTag === "Search" && (
              <div className="sub-category-segmented">
                <CustomSegmented
                  isFixedTags={false}
                  options={categoryOptions}
                  value={activeCategory}
                  onChange={setActiveCategory}
                  block
                  isBackground={false}
                />
              </div>
            )}
          </div>

          <div className="use-cases-grid">
            {(displayedItems.length === 0 ? placeholderData : displayedItems).map((item, index) =>
              // {(1 ? placeholderData : displayedItems).map((item, index) => (
              item.isPlaceholder ? (
                <SkeletonItem key={`skeleton-${index}`} />
              ) : (
                <div
                  key={item.id}
                  className="use-case-card"
                  onClick={() => {
                    if (item.url) {
                      window.open(item.url);
                    }
                    if (item.video) {
                      setCurentVideo({
                        src: item.video,
                        title: item.title,
                        description: item.description,
                        deliverables: item.deliverables || [],
                      });
                      setAdopterModalStatus(true);
                    }
                  }}
                >
                  <div className="use-case-card-image">
                    {renderContent(item.video ? "video" : "img", item.video ? item.video : item.picture)}
                  </div>
                  <div className="use-case-card-content">
                    <h3 className="use-case-card-title">{item.title}</h3>
                    <p className="use-case-card-description">{item.description}</p>
                  </div>
                  <div className="use-case-card-tag-box">
                    <div className="use-case-card-tag">{item.category || item.tag}</div>
                  </div>
                </div>
              )
            )}
          </div>
          <div className="fellou-case-info-box">
            <div className="fellou-case-info">
              {displayedItems.length < filteredItems.length || !showAll ? (
                <FellouButton
                  className="fellou-view-all-btn"
                  mode="white-border-transparent-bg"
                  onClick={() => handleViewMore("official")}
                >
                  <>
                    More
                    <i
                      className="icon iconfont icon-arrow-left1-line"
                      style={{ transform: "rotate(270deg)", display: "inline-block", fontSize: "16px" }}
                    ></i>
                  </>
                </FellouButton>
              ) : null}
            </div>
          </div>

          {/* Always render the anchor point for user-cases-section */}

          {/* User Provided Cases Section */}
          {userfilteredItems.length > 0 && (
            <div className="user-provided-cases-section" id="user-cases-section-anchor">
              <div className="use-case-title">Explore amazing use cases from Fellou users</div>
              <div className="use-cases-grid">
                {displayedUserItems.map((item, index) => (
                  <div
                    key={`user-${item.id || index}`}
                    className="use-case-card"
                    onClick={() => {
                      if (item.url) {
                        window.open(item.url);
                      }
                      if (item.video) {
                        setCurentVideo({
                          src: item.video,
                          title: item.title,
                          description: item.description,
                          deliverables: item.deliverables || [],
                        });
                        setAdopterModalStatus(true);
                      }
                    }}
                  >
                    <div className="use-case-card-content">
                      <p className="use-case-card-tag">
                        <Image className="quote-icon" src={QuoteIcon} alt="fellou" />
                      </p>
                      <p className="use-case-card-description">{item.description}</p>
                    </div>
                    <div className="use-case-card-bottom-box">
                      <Image className="use-case-card-icon" src={ReportIcon} alt="fellou" />
                      <div className="use-case-card-bottom-title-box">
                        <h3 className="use-case-card-title">{item.title}</h3>
                        <div className="desc-btn">click to open report</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {displayedUserItems.length < userfilteredItems.length || !showAllUserItems ? (
                <div className="fellou-case-info-box">
                  <div className="fellou-case-info">
                    <FellouButton
                      className="fellou-view-all-btn"
                      mode="white-border-transparent-bg"
                      onClick={() => handleViewMore("user")}
                    >
                      <>
                        More
                        <i
                          className="icon iconfont icon-arrow-left1-line"
                          style={{ transform: "rotate(270deg)", display: "inline-block", fontSize: "16px" }}
                        ></i>
                      </>
                    </FellouButton>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          <VideoModal
            src={curentVideo.src}
            title={curentVideo.title}
            description={curentVideo.description}
            deliverables={curentVideo.deliverables}
            open={adopterModalStatus}
            setOpen={setAdopterModalStatus}
          />
        </div>
      </div>
    </div>
  );
}
